import {
  getPointOfInterestMarkerProps,
  getStationMarkerProps,
} from '../helpers/map-marker';

import { PointOfInterestType } from '@/models/enums';

import type { FcwEntitiesMapLegendItemProps } from '@/components/organisms/FcwEntitiesMapLegend/components/FcwEntitiesMapLegendItem.vue';

interface UseLegendItemsReturn {
  /**
   * Stations items for entities map legend
   */
  stationItems: ComputedRef<FcwEntitiesMapLegendItemProps[]>;
  /**
   * Points of interest items for entities map legend
   */
  pointOfInterestItems: ComputedRef<FcwEntitiesMapLegendItemProps[]>;
}

export function useLegendItems(items: {
  stations: Ref<MapStation[]>;
  pointsOfInterest: Ref<MapPointOfInterest[]>;
}): UseLegendItemsReturn {
  const { stations, pointsOfInterest } = items;

  const { theme } = useClientStore().clientState;

  const firstPhysicalStation = computed<MapStation>(() => {
    return (
      stations.value.find(
        station => !station.isVirtual && !station.isClosed
      ) ?? {
        // Fallback to a default station if no physical station is found, as legend must be displayed at all times
        availableBikes: 4,
        isVirtual: false,
        isClosed: false,
        closedUntil: null,
        availableSlots: 0,
        coordinates: [0, 0],
        id: '1',
        label: 'X',
      }
    );
  });
  const firstVirtualStation = computed<MapStation | null>(() => {
    return stations.value.find(station => station.isVirtual) ?? null;
  });

  const stationItems = computed<FcwEntitiesMapLegendItemProps[]>(() => {
    return [
      {
        title: t('entities_map__legend__physical_station__title'),
        description: t('entities_map__legend__physical_station__description'),
        icon: getStationMarkerProps(firstPhysicalStation.value, false, theme),
      },
      ...(firstVirtualStation.value
        ? [
            {
              title: t('entities_map__legend__virtual_station__title'),
              description: t(
                'entities_map__legend__virtual_station__description'
              ),
              icon: getStationMarkerProps(
                firstVirtualStation.value,
                false,
                theme
              ),
            },
          ]
        : []),
      {
        title: t('entities_map__legend__station_closed__title'),
        description: t('entities_map__legend__station_closed__description'),
        icon: getStationMarkerProps(
          { ...firstPhysicalStation.value, isClosed: true },
          false,
          theme
        ),
      },
    ];
  });

  const isSomeBikeLocker = computed<boolean>(() => {
    return pointsOfInterest.value.some(
      pointOfInterest => pointOfInterest.type === PointOfInterestType.BikeLocker
    );
  });
  const isSomeBikeHouse = computed<boolean>(() => {
    return pointsOfInterest.value.some(
      pointOfInterest => pointOfInterest.type === PointOfInterestType.BikeHouse
    );
  });

  const { t } = useClientI18n();

  const pointOfInterestItems = computed<FcwEntitiesMapLegendItemProps[]>(() => {
    return [
      ...(isSomeBikeLocker.value
        ? [
            {
              title: t('entities_map__legend__bike_locker__title'),
              description: t('entities_map__legend__bike_locker__description'),
              icon: getPointOfInterestMarkerProps(
                {
                  type: PointOfInterestType.BikeLocker,
                } as MapPointOfInterest,
                theme
              ),
            },
          ]
        : []),
      ...(isSomeBikeHouse.value
        ? [
            {
              title: t('entities_map__legend__bike_house__title'),
              description: t('entities_map__legend__bike_house__description'),
              icon: getPointOfInterestMarkerProps(
                { type: PointOfInterestType.BikeHouse } as MapPointOfInterest,
                theme
              ),
            },
          ]
        : []),
    ];
  });

  return { stationItems, pointOfInterestItems };
}
